import { useQueryClient } from '@tanstack/react-query';
import { getListOrganizationDomainsQueryKey, useCreateOrganizationDomain } from '@sara/api-client';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@sara/ui/components/dialog';
import type { DialogBaseProps } from '@sara/ui/components/dialog-provider';
import { toast } from '@sara/ui/components/sonner';
import { CreateDomainForm, type CreateDomainFormValues } from '../../components/CreateDomainForm';

interface CreateDomainDialogProps extends DialogBaseProps {
  orgId: string;
}

function CreateDomainDialog({ orgId, onClose }: CreateDomainDialogProps) {
  const queryClient = useQueryClient();

  const { mutate, isPending, error } = useCreateOrganizationDomain({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationDomainsQueryKey({ orgId }),
        });
        toast.success(
          'Domain requested — ops will attach it and CNAMEs will appear on this page shortly.'
        );
        onClose();
      },
    },
  });

  function handleSubmit(values: CreateDomainFormValues) {
    mutate({ data: { hostname: values.hostname }, params: { orgId } });
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium tracking-tight">
          Add custom domain
        </DialogTitle>
        <DialogDescription>
          Use your own hostname for the candidate-facing interview link. Once requested, ops will
          attach it to our edge and CNAME records will appear on this page.
        </DialogDescription>
      </DialogHeader>
      <CreateDomainForm
        onSubmit={handleSubmit}
        onCancel={onClose}
        isPending={isPending}
        error={error instanceof Error ? error.message : error ? 'Failed to add domain' : null}
      />
    </DialogContent>
  );
}

export { CreateDomainDialog };
